
import moment from 'moment';

import { FormattedMessage } from 'react-intl';

import { formatDateCheckInOut, formatLocationName, groupHotelByPackageDate, groupByCondoDateRange, groupByCarDateRange } from '@share/utils';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import { InputFilterType } from '@share/common-types';
import { IColumnMenu } from '@constants';
import { Tooltip, Popover } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHotel, faCar, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { isEmpty } from 'lodash';

const MobileFirstColumnBase: IColumnMenu = {
  description: 'Description',
  key: 'description',
  type: InputFilterType.Custom,
  display: true,
  order: 1,
};

const FirstColumnBase: IColumnMenu = {
  description: 'Actions',
  key: 'actions',
  width: 150,
  type: InputFilterType.Custom,
  display: true,
  order: 1,
};

export enum Type {
  Condos,
  Hotels,
  Cars,
  Activities,
  Historicals,
  Packages
};

export type VisibleFunction = (visible: boolean, record?: any) => void;
export type QuoteBaseFunction = (quoteBase: string) => void;

const getActions = (_type: Type, isMobile: boolean, record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => {
  const quoteUrl = `${import.meta.env.VITE_API_BFF_URL}/deals/quote/${record.quoteBase}/file`;
  return (
    <>
      <div style={{ marginTop: isMobile ? '15px' : '0px' }}>
        <a
          href="#"
          rel="noreferrer"
          onClick={(e) => {
            e.preventDefault();
            setVisible(true, record);
            setQuoteBase(record.quoteBase);
            markQuotesAsRead(record.quoteBase);
          }}
        >
          <FormattedMessage id={record?.isExpired ? 'results.hotel.card.compare.modal.view.only' : 'results.hotel.card.compare.modal.view'} />
        </a>
      </div>
      <div>
        <a
          href={quoteUrl}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            markQuotesAsRead(record.quoteBase);
          }}
        >
          Download Quote
        </a>
      </div>
    </>)
};

const getMobileBaseColumn = (type: Type, record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, getExtraColumns: (record: any) => JSX.Element, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    <div style={{ marginBottom: '6px', fontSize: '12px' }}>
      <span><strong>Creation Date:&nbsp;</strong>{moment(record.creationDate, 'YYYY-MM-DD').format('MM/DD/YYYY')}</span>
    </div>
    {getExtraColumns(record)}
    <div style={{ marginBottom: '6px', fontSize: '12px' }}>
      <span><strong>City:&nbsp;</strong>{renderLocationCell(record?.places)}</span>
    </div>
    {!isEmpty(record.comment) && (
      <div style={{ fontSize: '12px' }}>
        <span><strong>Comment:&nbsp;</strong>{record.comment}</span>
      </div>)}
    {getActions(type, true, record, setVisible, setQuoteBase, markQuotesAsRead)}
  </>);

const getHotelsMobileColumn = (record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    {getMobileBaseColumn(Type.Hotels, record, setVisible, setQuoteBase, (record: any) => getHotelsMobileExtraColumn(record), markQuotesAsRead)}
  </>);

const getHotelsMobileExtraColumn = (record: any) => (
  <>
    <div style={{ marginBottom: '6px', fontSize: '12px' }}>
      <span><strong>Check In - Check Out:&nbsp;</strong>{moment(record.checkIn, 'YYYY-MM-DD').format('MM/DD/YYYY')} - {moment(record.checkOut, 'YYYY-MM-DD').format('MM/DD/YYYY')}</span>
    </div>
  </>);

const getAvailableDateText = (availableDates: any[]) => (
  <div style={{ display: 'block' }}>
    {availableDates?.map((date, index) => (
      <label key={index} style={{ marginRight: '6px', whiteSpace: 'nowrap' }}>{index + 1}:<strong style={{ marginLeft: '6px' }}>({date.availableDates?.length ? date.availableDates.length : 'NA'})</strong>,</label>
    ))}
  </div>
);

const getCondosMobileColumn = (record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    {getMobileBaseColumn(Type.Condos, record, setVisible, setQuoteBase, (record: any) => getCondosMobileExtraColumn(record), markQuotesAsRead)}
  </>);

const getCondosMobileExtraColumn = (record: any) => (
  <>
    <div style={{ marginBottom: '6px', fontSize: '12px' }}>
      <strong>Available Dates:&nbsp;</strong>{getAvailableDateText(record.availableDates)}
    </div>
  </>);

const getCarsMobileColumn = (record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    {getMobileBaseColumn(Type.Hotels, record, setVisible, setQuoteBase, (record: any) => getCarsMobileExtraColumn(record), markQuotesAsRead)}
  </>);

const getCarsMobileExtraColumn = (record: any) => (
  <>
    <div style={{ marginBottom: '6px', fontSize: '12px' }}>
      <span><strong>Pick-uo - Drop-off dates:&nbsp;</strong>{moment(record.checkIn, 'YYYY-MM-DD').format('MM/DD/YYYY')} - {moment(record.checkOut, 'YYYY-MM-DD').format('MM/DD/YYYY')}</span>
    </div>
  </>);

const getActivitiesMobileColumn = (record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    {getMobileBaseColumn(Type.Activities, record, setVisible, setQuoteBase, (record: any) => getActivitiesMobileExtraColumn(record), markQuotesAsRead)}
  </>);  

const getActivitiesMobileExtraColumn = (record: any) => (
  <>
    <div style={{ marginBottom: '6px', fontSize: '12px' }}>
      <span><strong>Dates:&nbsp;</strong>{moment(record.checkIn, 'YYYY-MM-DD').format('MM/DD/YYYY')} - {moment(record.checkOut, 'YYYY-MM-DD').format('MM/DD/YYYY')}</span>
    </div>
  </>);

const getPackagesMobileColumn = (record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    {getMobileBaseColumn(Type.Packages, record, setVisible, setQuoteBase, (record: any) => getPackagesMobileExtraColumn(record), markQuotesAsRead)}
  </>);

const getPackagesMobileExtraColumn = (record: any) => (
  <>
    <div style={{ marginBottom: '6px', fontSize: '12px' }}>
      <span><strong>Check In - Check Out:&nbsp;</strong>{moment(record.checkIn, 'YYYY-MM-DD').format('MM/DD/YYYY')} - {moment(record.checkOut, 'YYYY-MM-DD').format('MM/DD/YYYY')}</span>
    </div>
  </>);


const getHistoricalMobileColumn = (record: any, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => (
  <>
    {getMobileBaseColumn(Type.Historicals, record, setVisible, setQuoteBase, (record: any) => {
      const getMobileColumn = record.isCondo ? getCondosMobileExtraColumn : getHotelsMobileExtraColumn;
      return getMobileColumn(record);
    }, markQuotesAsRead)}
  </>);

// Grouping helpers live in @share/utils so the store slice can reuse the exact same
// logic when deciding which quotes are real (multi-quote) packages. Re-exported here so
// existing imports from this module (e.g. the package compare modal) keep working.
export { groupHotelByPackageDate, groupByCondoDateRange, groupByCarDateRange } from '@share/utils';

const renderPackageDetails = (record: any) => {
  const { packageQuotes } = record;
  if (!packageQuotes) return null;

  const { hotelQuotes = [], condoQuotes = [], carQuotes = [] } = packageQuotes;

  const renderGroup = (title: string, items: any[], icon: any, color: string, type: string) => {
    if (!items.length) return null;

    let groupedData: any[] = [];
    if (type === 'Hotel') groupedData = groupHotelByPackageDate(items);
    else if (type === 'Condo') groupedData = groupByCondoDateRange(items);
    else if (type === 'Car') groupedData = groupByCarDateRange(items);

    const groupedByLocation: Record<string, any[]> = {};
    groupedData.forEach((group) => {
      const firstItem = group.items[0];
      let locationName = 'Unknown Location';
      if (type === 'Hotel') {
        try {
          const parsed = firstItem.location ? JSON.parse(firstItem.location) : null;
          locationName = parsed?.name || firstItem.place || 'Unknown Location';
        } catch (e) {
          locationName = firstItem.place || 'Unknown Location';
        }
      } else if (type === 'Condo') {
        locationName = firstItem.locationObj?.name || firstItem.place || 'Unknown Location';
      } else if (type === 'Car') {
        const pickUp = firstItem.pickUpLocationObj?.name || firstItem.pickUpLocationDetail || 'Unknown';
        const dropOff = firstItem.dropOffLocationObj?.name || firstItem.dropOffLocationDetail || pickUp;
        locationName = pickUp === dropOff ? pickUp : `${pickUp} -> ${dropOff}`;
      }
      
      locationName = formatLocationName(locationName);
      if (!groupedByLocation[locationName]) groupedByLocation[locationName] = [];
      groupedByLocation[locationName].push(group);
    });

    const content = (
      <div style={{ maxWidth: '500px', maxHeight: '500px', overflowY: 'auto', padding: '4px' }}>
        {Object.entries(groupedByLocation).map(([locationName, groups], locIdx) => (
          <div key={locIdx} style={{ marginBottom: locIdx === Object.keys(groupedByLocation).length - 1 ? '0' : '16px' }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: '13px',
                color: '#1890ff',
                marginBottom: '8px',
                paddingLeft: '4px',
                borderLeft: '3px solid #1890ff',
                textTransform: 'capitalize'
              }}
            >
              {locationName}
            </div>
            {groups.map((group, groupIdx) => {
              let guestInfo = '';
              if (type === 'Hotel' && group.rooms) {
                guestInfo = ` | ${group.rooms}`;
              } else if (type === 'Condo') {
                const first = group.items[0];
                const condoGuestDetail = (group.roomsDetail || first.roomsDetail || first.occupancy || '').toString().trim();
                if (condoGuestDetail) {
                  guestInfo = ` | ${condoGuestDetail}`;
                } else if (first.adults !== undefined) {
                  guestInfo = ` | ${first.adults} Adults, ${first.kids || 0} Kids`;
                }
              } else if (type === 'Car') {
                guestInfo = ` | ${group.pickUpTime || ''} - ${group.returnTime || ''}${group.driverAge ? ` | ${group.driverAge} Yrs` : ''}${group.countryISO ? ` | ${group.countryISO}` : ''}`;
              }

              return (
                <div
                  key={groupIdx}
                  style={{
                    marginBottom: groupIdx === groups.length - 1 ? '0' : '12px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginLeft: '8px'
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: '11px',
                      background: '#fafafa',
                      padding: '4px 10px',
                      borderBottom: '1px solid #f0f0f0',
                      color: '#1a1a1a',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <span>
                      {group.searchType === 'Flexible' ? (
                        group.selectedMonths?.length ? (
                          group.selectedMonths.map((m: string) => moment(m).format("MMMM YYYY")).join(" - ")
                        ) : (
                          <FormattedMessage id="anytime" defaultMessage="Anytime" />
                        )
                      ) : (
                        <>
                          {formatDateCheckInOut(group.startDate, DATE_FORMAT_CHECK_IN_OUT)} -{' '}
                          {formatDateCheckInOut(group.endDate, DATE_FORMAT_CHECK_IN_OUT)}
                        </>
                      )}
                      <span style={{ color: '#8c8c8c', fontWeight: 400 }}>
                        {guestInfo}
                      </span>
                    </span>

                    <span
                      style={{
                        background: color,
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontSize: '10px',
                        color: '#fff'
                      }}
                    >
                      {group.items.length}
                    </span>
                  </div>

                  <div style={{ padding: '6px 10px' }}>
                    {group.items.map((q: any, idx: number) => {
                      const name = q.packageQuotes
                        ? 'Package'
                        : q.displayName ||
                        q.hotelName ||
                        q.condoName ||
                        q.location ||
                        q.pickUpLocationDetail ||
                        'Item';

                      const detail =
                        q.roomName || q.carType || q.detail || '';

                      return (
                        <div
                          key={idx}
                          style={{
                            marginBottom: idx === group.items.length - 1 ? '0' : '4px',
                            fontSize: '11px',
                            color: '#555'
                          }}
                        >
                          {idx + 1}. {name} {detail ? `(${detail})` : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );


    return (
      <Popover content={content} title={`${title} (${groupedData.length})`} trigger="hover" placement="bottomLeft">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px 10px',
          borderRadius: '4px',
          background: '#f0f7ff',
          cursor: 'pointer',
          border: `1px solid ${color}40`,
          transition: 'all 0.3s'
        }}>
          <FontAwesomeIcon icon={icon} style={{ color, marginRight: '8px', fontSize: '14px' }} />
          <span style={{ fontWeight: 500, fontSize: '13px', color: '#1a1a1a' }}>{title} ({groupedData.length})</span>
        </div>
      </Popover>
    );
  };

  return (
    <div className="package-details-column" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '4px 0' }}>
      {renderGroup('Hotels', hotelQuotes, faHotel, '#1890ff', 'Hotel')}
      {renderGroup('Condos', condoQuotes, faBuilding, '#1890ff', 'Condo')}
      {renderGroup('Cars', carQuotes, faCar, '#52c41a', 'Car')}
    </div>
  );
};

// Renders a location cell: a single place as plain text, or, when a quote spans several
// places, "Multiple locations" with a View link that opens a popover listing them.
const renderLocationCell = (places: string[] = []) => {
  if (!places?.length) return null;
  if (places.length === 1) return <span>{places[0]}</span>;

  const content = (
    <div style={{ display: 'block', maxWidth: '280px' }}>
      {places.map((place, index) => (
        <div key={index} style={{ padding: '2px 0' }}>{place}</div>
      ))}
    </div>
  );

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <FormattedMessage id="quotes.multiple.locations" defaultMessage="Multiple locations" />
      <Popover content={content} title={<FormattedMessage id="quotes.locations" defaultMessage="Locations" />} trigger="click" placement="bottomRight">
        <a href="#" onClick={(e) => e.preventDefault()} style={{ whiteSpace: 'nowrap' }}>
          <FormattedMessage id="quotes.view" defaultMessage="View" />
        </a>
      </Popover>
    </span>
  );
};

const ColumnsHotelsBase: IColumnMenu[] = [
  { description: 'Creation Date', key: 'creationDate', width: 130, display: true, order: 1, type: InputFilterType.Text, displayConditions: true },
  { description: 'Check In', key: 'checkIn', width: 120, display: true, order: 2, type: InputFilterType.Text, displayConditions: true },
  { description: 'Check Out', key: 'checkOut', width: 120, display: true, order: 3, type: InputFilterType.Text, displayConditions: true },
  { description: 'City', key: 'place', width: 300, display: true, order: 4, type: InputFilterType.Custom, component: (_text: any, record?: any) => renderLocationCell(record?.places) },
  { description: 'Comment', key: 'comment', width: 230, display: true, order: 5, type: InputFilterType.Text, displayConditions: false },
];

const ColumnsCondosBase: IColumnMenu[] = [
  { description: 'Creation Date', key: 'creationDate', width: 130, display: true, order: 1, type: InputFilterType.Text, displayConditions: true },
  {
    description: 'Available Dates',
    key: 'availableDates',
    width: 250,
    display: true,
    order: 2,
    type: InputFilterType.Custom,
    component: (_text: any, record?: any) => {
      const availableDateText = getAvailableDateText(record.availableDates);
      return <Tooltip title={availableDateText}>{availableDateText}</Tooltip>;
    }
  },
  { description: 'City', key: 'place', width: 300, display: true, order: 4, type: InputFilterType.Custom, component: (_text: any, record?: any) => renderLocationCell(record?.places) },
  { description: 'Comment', key: 'comment', width: 230, display: true, order: 5, type: InputFilterType.Text, displayConditions: false },
];

const ColumnsCarsBase: IColumnMenu[] = [
  { description: 'Creation Date', key: 'creationDate', width: 130, display: true, order: 1, type: InputFilterType.Text, displayConditions: true },
  { description: 'Check In', key: 'checkIn', width: 120, display: true, order: 2, type: InputFilterType.Text, displayConditions: true },
  { description: 'Check Out', key: 'checkOut', width: 120, display: true, order: 3, type: InputFilterType.Text, displayConditions: true },
  { description: 'PickUp', key: 'pickUpLocationDetail', width: 300, display: true, order: 4, type: InputFilterType.Custom, component: (_text: any, record?: any) => renderLocationCell(record?.pickUpLocations) },
  { description: 'Comment', key: 'comment', width: 230, display: true, order: 5, type: InputFilterType.Text, displayConditions: false },
];

const ColumnsActivitiesBase: IColumnMenu[] = [
  { description: 'Creation Date', key: 'creationDate', width: 130, display: true, order: 1, type: InputFilterType.Text, displayConditions: true },
  { description: 'Date', key: 'checkIn', width: 120, display: true, order: 2, type: InputFilterType.Text, displayConditions: true },
  { description: 'Location', key: 'location', width: 300, display: true, order: 4, type: InputFilterType.Text, displayConditions: false },
  { description: 'Comment', key: 'comment', width: 230, display: true, order: 5, type: InputFilterType.Text, displayConditions: false },
];

const ColumnsHistoricalBase: IColumnMenu[] = [
  { description: 'Creation Date', key: 'creationDate', width: 130, display: true, order: 1, type: InputFilterType.Text, displayConditions: true },
  {
    description: 'Type',
    key: 'type',
    type: InputFilterType.Custom,
    width: 90,
    display: true,
    order: 2,
    component: (_text: any, record?: any) => (<div> <FormattedMessage id={record?.quoteType === 'Hotel' ? 'Hotel' : record?.quoteType === 'Car' ? 'Car' : record?.quoteType === 'Activity' ? 'Activity' : record?.isCondo ? 'Condo' : 'Hotel'} /></div>)
  },
  {
    description: 'Dates',
    key: 'dates',
    width: 200,
    display: true,
    order: 4,
    type: InputFilterType.Custom,
    component: (_text: any, record?: any) => {
      const isCondo = record.isCondo;
      if (!isCondo) {
        const dates = `${record.checkIn} - ${record.checkOut}`;
        return <Tooltip title={dates}>{dates}</Tooltip>;
      }
      const availableDateText = getAvailableDateText(record.availableDates);
      return <Tooltip title={availableDateText}>{availableDateText}</Tooltip>;
    }
  },
  { description: 'City', key: 'place', width: 280, display: true, order: 5, type: InputFilterType.Custom, component: (_text: any, record?: any) => renderLocationCell(record?.places) },
  { description: 'Comment', key: 'comment', width: 200, display: true, order: 6, type: InputFilterType.Text, displayConditions: false },
];

const GetColumns = (type: Type, isMobile: boolean, setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => {
  if (isMobile) {
    // const getMobileColumn = type === Type.Condos ? getCondosMobileColumn : type === Type.Hotels ? getHotelsMobileColumn : getHistoricalMobileColumn;
    let getMobileColumn;
    switch (type) {
      case Type.Condos:
        getMobileColumn = getCondosMobileColumn;
        break;
      case Type.Hotels:
        getMobileColumn = getHotelsMobileColumn;
        break;
      case Type.Cars:
        getMobileColumn = getCarsMobileColumn;
        break;
      case Type.Activities:
        getMobileColumn = getActivitiesMobileColumn;  
        break;
      case Type.Packages:
        getMobileColumn = getPackagesMobileColumn;
        break;
      default:
        getMobileColumn = getHistoricalMobileColumn;
    }
    return [{
      ...MobileFirstColumnBase,
      component: (_text: string, record: any) => getMobileColumn(record, setVisible, setQuoteBase, markQuotesAsRead)
    }];
  } else {
    const ColumnsPackagesBase: IColumnMenu[] = [
      { description: 'Creation Date', key: 'creationDate', width: 130, display: true, order: 1, type: InputFilterType.Text, displayConditions: true },
      {
        description: 'Details',
        key: 'details',
        width: 650,
        display: true,
        order: 2,
        type: InputFilterType.Custom,
        component: (_text: any, record?: any) => renderPackageDetails(record)
      },
      { description: 'Comment', key: 'comment', width: 230, display: true, order: 5, type: InputFilterType.Text, displayConditions: false },
    ];

    const columnsBase = type === Type.Condos ? ColumnsCondosBase : type === Type.Hotels ? ColumnsHotelsBase : type === Type.Cars ? ColumnsCarsBase : type === Type.Packages ? ColumnsPackagesBase : ColumnsHistoricalBase;
    return [{
      ...FirstColumnBase,
      component: (_text: string, record: any) => { return getActions(type, false, record, setVisible, setQuoteBase, markQuotesAsRead) }
    },
    ...columnsBase
    ];
  }
}

export const GetQuoteDatasourceData = (quote: any, originalList?: any[]) => {
  const isCondo = quote.isCondo;

  // All quotes sharing this quoteBaseId (the row's group). Used to detect when a single
  // quote spans more than one location.
  const group = originalList?.length ? originalList.filter((q: any) => q.quoteBaseId === quote.quoteBaseId) : [quote];
  const places = Array.from(new Set(group.map((q: any) => q.place).filter(Boolean)));
  const pickUpLocations = Array.from(new Set(group.map((q: any) => q.pickUpLocationDetail).filter(Boolean)));

  const data = {
    quoteBase: quote.quoteBaseId,
    creationDate: formatDateCheckInOut(quote.creationDate, DATE_FORMAT_CHECK_IN_OUT),
    place: quote.place,
    places,
    pickUpLocations,
    comment: quote.comment,
    fileUrl: quote.fileUrl,
    isCondo,
    isExpired: quote.isExpired,
    quoteType: quote.quoteType,
    location: quote.location,
    pickUpLocationDetail: quote.pickUpLocationDetail
  } as any;

  if (quote.packageQuotes) {
    data.packageQuotes = quote.packageQuotes;
    const allNestedQuotes = [
      ...(quote.packageQuotes.hotelQuotes || []),
      ...(quote.packageQuotes.condoQuotes || []),
      ...(quote.packageQuotes.carQuotes || []),
    ];
    if (allNestedQuotes.length > 0) {
      const checkIns = allNestedQuotes.map(q => q.checkIn).filter(Boolean).sort();
      const checkOuts = allNestedQuotes.map(q => q.checkOut).filter(Boolean).sort();

      if (checkIns.length > 0) data.checkIn = formatDateCheckInOut(checkIns[0], DATE_FORMAT_CHECK_IN_OUT);
      if (checkOuts.length > 0) data.checkOut = formatDateCheckInOut(checkOuts[checkOuts.length - 1], DATE_FORMAT_CHECK_IN_OUT);
    }
  } else if (isCondo) {
    data.availableDates = originalList?.filter(q => q.quoteBaseId === quote.quoteBaseId).map(({ availableDates }) => ({ availableDates }));
  } else {
    data.checkIn = formatDateCheckInOut(quote.checkIn, DATE_FORMAT_CHECK_IN_OUT);
    data.checkOut = formatDateCheckInOut(quote.checkOut, DATE_FORMAT_CHECK_IN_OUT);
  }

  return data;
}

export const HOTELS_KEY = '1';
export const CONDOS_KEY = '2';
export const CARS_KEY = '3';
export const ACTIVITIES_KEY = '5';
export const HISTORICAL_KEY = '4';
export const PACKAGES_KEY = '6';

// Every tab opens the same standardized package compare modal, so a single setVisible is
// shared across all of them.
export const GetMyQuotesTabs = (isMobile: boolean, dataSourceHotels: any[], dataSourceCondos: any[], dataSourceCars: any[], dataSourceActivities: any[], dataSourcePackages: any[], setVisible: VisibleFunction, setQuoteBase: QuoteBaseFunction, markQuotesAsRead: (quoteBase: string) => void) => {
  return [
    {
      key: HOTELS_KEY,
      title: 'hotels',
      columns: GetColumns(Type.Hotels, isMobile, setVisible, setQuoteBase, markQuotesAsRead),
      datasource: dataSourceHotels
    },
    {
      key: CONDOS_KEY,
      title: 'condos',
      columns: GetColumns(Type.Condos, isMobile, setVisible, setQuoteBase, markQuotesAsRead),
      datasource: dataSourceCondos
    },
    {
      key: CARS_KEY,
      title: 'cars',
      columns: GetColumns(Type.Cars, isMobile, setVisible, setQuoteBase, markQuotesAsRead),
      datasource: dataSourceCars
    },
    {
      key: ACTIVITIES_KEY,
      title: 'activities',
      columns: GetColumns(Type.Activities, isMobile, setVisible, setQuoteBase, markQuotesAsRead),
      datasource: dataSourceActivities
    },
    {
      key: PACKAGES_KEY,
      title: 'package',
      columns: GetColumns(Type.Packages, isMobile, setVisible, setQuoteBase, markQuotesAsRead),
      datasource: dataSourcePackages
    },
  ];

}
